/* Imports */
import React, { useState } from "react"
import { Font, Section, Grid, Input, Flexbox, InputCheck } from "fajny-react"
import { Link } from "react-router-dom"
import { v4 as uuid } from "uuid"

import PageLayout from "../../components/PageLayout"
import CodeContainer from "../../components/CodeContainer"
import CardIcon from "../../components/CardIcon"

import allIcons from "../../data/all-icons"

const ListIconsPage = () => {
    const [search, setSearch] = useState("")
    const handleSearch = e => setSearch(e.target.value)
    const clearSearch = () => setSearch("")

    let results = allIcons
        .sort((a, b) => {
            return a.name > b.name ? 1 : -1
        })
        .filter(icon => icon.name.toLowerCase().includes(search.toLowerCase()))

    const [filter, setFilter] = useState("all")

    const handleFilter = e => {
        if (e.target.checked) {
            setFilter(e.target.value)
        }
    }

    if (filter !== "all") {
        results = results.filter(icon => icon.category === filter)
    }

    return (
        <PageLayout title="Icons">
            <Font.H1>Icons</Font.H1>

            <Font.P>
                Fanjy CSS uses its own icons. Most of icons have two weights:
                regular and solid. When using them, try to stick to one weight.
            </Font.P>

            <Section gap="large">
                <Font.H3>How to use</Font.H3>

                <Section gap="small" as="div">
                    <Font.P>
                        First, import the <code>FajnyIcon</code> component:
                    </Font.P>

                    <CodeContainer>
                        {`import { FajnyIcon } from "fajny-react"`}
                    </CodeContainer>
                </Section>

                <Section gap="small" as="div">
                    <Font.P>Then, add it in your JSX:</Font.P>

                    <CodeContainer>
                        {`<FajnyIcon name="name-of-icon" size={24} color="secondary" />`}
                    </CodeContainer>

                    <Font.P>
                        And replace <code>name-of-icon</code> by the real name
                        of the icon. Size has to be a number, and for the color
                        you can either use one of{" "}
                        <Link to="/styles/colors">Fajny colors</Link>, or any
                        other color.
                    </Font.P>
                </Section>
            </Section>

            <Section gap="small">
                <Font.H3>As a Styled component mixin</Font.H3>

                <Font.P>You can also use our icons in a mixin:</Font.P>

                <CodeContainer>
                    {
                        // eslint-disable-next-line
                        'import { Mixins } from "fajny-react"\n\nconst Component = styled.div`\n    &:before {\n        ${Mixins.Icon({\n            $name: "name-of-icon",\n            $color: "secondary-400",\n            $size: 48,\n        })}\n    }\n`'
                    }
                </CodeContainer>
            </Section>

            <Section gap="medium">
                <Grid col={3} gap="s">
                    <Input
                        label="Search"
                        fajnyIcon="magnifying-glass-solid"
                        placeholder="Search icon"
                        id="search"
                        onChange={handleSearch}
                        value={search}
                        type="search"
                        clearSearch={clearSearch}
                    />
                </Grid>

                <Flexbox columnGap="xs" rowGap="s" wrap="wrap">
                    <InputCheck
                        type="radio"
                        label="All"
                        onChange={handleFilter}
                        value="all"
                        defaultChecked={filter === "all"}
                        id="filter-all"
                        name="filter-categories"
                    />
                    <InputCheck
                        type="radio"
                        label="User interface"
                        onChange={handleFilter}
                        value="user-interface"
                        defaultChecked={filter === "user-interface"}
                        id="filter-user-interface"
                        name="filter-categories"
                    />
                    <InputCheck
                        type="radio"
                        label="Brand"
                        onChange={handleFilter}
                        value="brand"
                        defaultChecked={filter === "brand"}
                        id="filter-brand"
                        name="filter-categories"
                    />
                    <InputCheck
                        type="radio"
                        label="Arrow"
                        onChange={handleFilter}
                        value="arrow"
                        defaultChecked={filter === "arrow"}
                        id="filter-arrow"
                        name="filter-categories"
                    />
                    <InputCheck
                        type="radio"
                        label="Shop"
                        onChange={handleFilter}
                        value="shop"
                        defaultChecked={filter === "shop"}
                        id="filter-shop"
                        name="filter-categories"
                    />
                    <InputCheck
                        type="radio"
                        label="Technology"
                        onChange={handleFilter}
                        value="technology"
                        defaultChecked={filter === "technology"}
                        id="filter-technology"
                        name="filter-categories"
                    />
                    <InputCheck
                        type="radio"
                        label="Misc"
                        onChange={handleFilter}
                        value="misc"
                        defaultChecked={filter === "misc"}
                        id="filter-misc"
                        name="filter-categories"
                    />
                    <InputCheck
                        type="radio"
                        label="Office"
                        onChange={handleFilter}
                        value="office"
                        defaultChecked={filter === "office"}
                        id="filter-office"
                        name="filter-categories"
                    />
                    <InputCheck
                        type="radio"
                        label="Communication"
                        onChange={handleFilter}
                        value="communication"
                        defaultChecked={filter === "communication"}
                        id="filter-communication"
                        name="filter-categories"
                    />
                    <InputCheck
                        type="radio"
                        label="Object"
                        onChange={handleFilter}
                        value="object"
                        defaultChecked={filter === "object"}
                        id="filter-object"
                        name="filter-categories"
                    />
                    <InputCheck
                        type="radio"
                        label="Date time & location"
                        onChange={handleFilter}
                        value="date-time-&-location"
                        defaultChecked={filter === "date-time-&-location"}
                        id="filter-date-time-&-location"
                        name="filter-categories"
                    />
                    <InputCheck
                        type="radio"
                        label="Math"
                        onChange={handleFilter}
                        value="math"
                        defaultChecked={filter === "math"}
                        id="filter-math"
                        name="filter-categories"
                    />
                    <InputCheck
                        type="radio"
                        label="People"
                        onChange={handleFilter}
                        value="people"
                        defaultChecked={filter === "people"}
                        id="filter-people"
                        name="filter-categories"
                    />
                </Flexbox>

                <Grid col={4} gap="m">
                    {results.map(icon => (
                        <CardIcon icon={icon} key={uuid()} />
                    ))}
                </Grid>
            </Section>
        </PageLayout>
    )
}

export default ListIconsPage
